import { Box, Stack, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import { GlassPanel } from '../../../shared/components/GlassPanel';
import { IconTile } from '../../../shared/components/IconTile';
import { Pill } from '../../../shared/components/Pill';
import { SoftButton } from '../../../shared/components/SoftButton';
import { hashSemanticColor } from '../../../shared/components/semanticColor';
import { usePermissions } from '../../../shared/hooks/usePermissions';
import type { AiAssistantRole, DocumentCategory, DocumentType } from '../../../core/types/data-models';
import { buildNextSteps } from './documentTypeCatalog';

/** "Next Steps" panel — the document types ready to generate next, styled as assistant messages. */
export function NextStepsSection({
  docTypes,
  categories,
  existingDocumentTypes,
  roles,
  onGenerate,
}: {
  docTypes: DocumentType[];
  categories: DocumentCategory[];
  existingDocumentTypes: Set<string>;
  roles: AiAssistantRole[];
  onGenerate: (docType: DocumentType) => void;
}) {
  const { canGenerate } = usePermissions();
  const items = buildNextSteps(docTypes, categories, existingDocumentTypes);
  const roleByCategory = new Map(roles.map((r) => [r.documentCategoryType, r]));

  return (
    <GlassPanel hover sx={{ p: 2.5, flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', maxHeight: 380 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ pb: 1.5, mb: 1.5, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
          <IconTile icon={<LightbulbIcon fontSize="inherit" />} color="primary" size="sm" />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={700} noWrap>
              Next Steps
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              AI suggestions to increase graph fidelity
            </Typography>
          </Box>
        </Stack>
        {items.length > 0 && (
          <Pill color="primary" sx={{ flexShrink: 0 }}>
            {items.length} Pending
          </Pill>
        )}
      </Stack>

      {items.length === 0 ? (
        <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
          You're all caught up — no more recommended documents right now.
        </Typography>
      ) : (
        <Stack spacing={1.25} sx={{ overflowY: 'auto', pr: 0.5 }}>
          {items.map(({ docType: dt }) => {
            const role = roleByCategory.get(dt.categoryType);
            const iconColor = hashSemanticColor(dt.categoryType);
            return (
              <Stack
                key={dt.id}
                direction="row"
                alignItems="flex-start"
                justifyContent="space-between"
                spacing={1.5}
                sx={{
                  p: 1.5,
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  boxShadow: (theme) => (theme.palette.mode === 'dark' ? 'none' : 'var(--elevation-sm)'),
                  transition: 'border-color var(--transition)',
                  '&:hover': { borderColor: 'var(--color-primary-line)' },
                }}
              >
                <Stack direction="row" spacing={1.25} sx={{ minWidth: 0 }}>
                  <IconTile
                    icon={<SmartToyOutlinedIcon fontSize="inherit" />}
                    color={iconColor}
                    size="sm"
                    sx={{ mt: 0.25 }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600}>
                      Generate{' '}
                      <Box component="span" sx={{ color: 'primary.main', fontWeight: 700 }}>
                        {dt.name}
                      </Box>
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                      {dt.description?.trim() || `${role?.name ?? 'AI Assistant'} recommends generating this artifact.`}
                    </Typography>
                  </Box>
                </Stack>
                {canGenerate && (
                  <SoftButton
                    tone="primary"
                    size="small"
                    endIcon={<ArrowForwardIcon sx={{ fontSize: '13px !important' }} />}
                    onClick={() => onGenerate(dt)}
                    sx={{ flexShrink: 0, fontSize: 11, fontWeight: 700 }}
                  >
                    Generate
                  </SoftButton>
                )}
              </Stack>
            );
          })}
        </Stack>
      )}
    </GlassPanel>
  );
}
